/**
 * Tela de Tarefas: a lista de trabalho do dia da Jaque e os follow-ups que o
 * Igor combina com os leads.
 *
 * "Hoje e atrasadas" e onde o dia comeca: concluir em um clique, adiar sem
 * abrir formulario e ir direto ao lead, pessoa, matricula ou contrato da
 * tarefa. "Todas" filtra por responsavel, tipo e status; "Concluidas" deixa
 * reabrir. A criacao rapida pede so titulo, tipo, vencimento e lembrete; o
 * resto fica no painel.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, RotateCcw } from 'lucide-react';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '../../layout/Shell.tsx';
import { useSessao } from '../../sessao.tsx';
import { ErroApi, escrever, ler, mensagemDe } from '../../ui/api.ts';
import {
  Aviso, Barra, BlocoInicio, Botao, Busca, Campo, Formulario, Painel, Selo, Tabela,
  type Coluna, type Tom,
} from '../../ui/componentes.tsx';
import { dataHora, hoje } from '../../ui/formatos.ts';
import type { TelaDeModulo } from '../contrato.ts';
import './comercial.css';

type TipoTarefa = 'ligar' | 'whatsapp' | 'email' | 'reuniao' | 'outro';
type VinculoTipo = 'lead' | 'pessoa' | 'matricula' | 'contrato';
type Situacao = 'atrasada' | 'hoje' | 'futura' | 'feita' | 'cancelada';

type Tarefa = {
  id: string;
  titulo: string;
  descricao: string | null;
  tipo: TipoTarefa;
  vinculo_tipo: VinculoTipo | null;
  vinculo_id: string | null;
  pessoa_id: string | null;
  pessoa_nome: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  vence_em: string;
  lembrar_em: string | null;
  lembrete_enviado_em: string | null;
  status: 'aberta' | 'feita' | 'cancelada';
  situacao: Situacao;
  dias_atraso: number;
  concluida_em: string | null;
  concluida_por_tipo: 'usuario' | 'chave_api' | null;
  concluida_por_nome: string | null;
  criado_em: string;
  atualizado_em: string;
};

type Lista = { itens: Tarefa[] };

const TIPOS: Record<TipoTarefa, string> = {
  ligar: 'Ligar', whatsapp: 'WhatsApp', email: 'E-mail', reuniao: 'Reunião', outro: 'Outro',
};

/** Para onde leva o vinculo. A tela de destino abre o registro pelo `?id=`. */
const VINCULOS: Record<VinculoTipo, { rotulo: string; rota: string }> = {
  lead: { rotulo: 'Lead', rota: '/leads' },
  pessoa: { rotulo: 'Pessoa', rota: '/pessoas' },
  matricula: { rotulo: 'Matrícula', rota: '/turmas' },
  contrato: { rotulo: 'Contrato', rota: '/contratos' },
};

/** Lembrete da criacao rapida, em minutos antes do vencimento. */
const LEMBRETES: { id: string; rotulo: string; minutos: number | null }[] = [
  { id: 'nenhum', rotulo: 'Sem lembrete', minutos: null },
  { id: 'na_hora', rotulo: 'Na hora', minutos: 0 },
  { id: '15m', rotulo: '15 minutos antes', minutos: 15 },
  { id: '1h', rotulo: '1 hora antes', minutos: 60 },
  { id: '1d', rotulo: '1 dia antes', minutos: 24 * 60 },
];

// datas: a tela fala em horario de Brasilia; o servidor entende AAAA-MM-DDTHH:MM assim

const BRASILIA = '-03:00';

/** Instante ISO -> "AAAA-MM-DDTHH:MM" em Sao Paulo, para o input datetime-local. */
function paraLocal(iso: string | null): string {
  if (iso === null) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const v = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')}T${v('hour')}:${v('minute')}`;
}

/** "AAAA-MM-DDTHH:MM" de Brasilia menos N minutos, como instante ISO. */
function menosMinutos(local: string, minutos: number): string {
  return new Date(Date.parse(`${local}:00${BRASILIA}`) - minutos * 60_000).toISOString();
}

/** Hoje + N dias (AAAA-MM-DD), em Sao Paulo. */
function diaMais(n: number): string {
  return new Date(Date.parse(`${hoje()}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Proxima segunda-feira (AAAA-MM-DD). */
function proximaSegunda(): string {
  const dia = new Date(`${hoje()}T12:00:00Z`).getUTCDay();
  return diaMais(((8 - dia) % 7) || 7);
}

const hora = (iso: string) => paraLocal(iso).slice(11);

/** O vencimento como a Jaque le: "Atrasada 3 dias", "Hoje 14:00", "05/11 09:00". */
function Vencimento({ t }: { t: Tarefa }) {
  const mapa: Record<Situacao, { tom: Tom; texto: string }> = {
    atrasada: {
      tom: 'erro',
      texto: t.dias_atraso > 0
        ? `Atrasada ${t.dias_atraso} ${t.dias_atraso === 1 ? 'dia' : 'dias'}`
        : `Atrasada, ${hora(t.vence_em)}`,
    },
    hoje: { tom: 'aviso', texto: `Hoje ${hora(t.vence_em)}` },
    futura: { tom: 'neutro', texto: dataHora(t.vence_em).replace(/\/\d{4}/, '') },
    feita: { tom: 'ok', texto: 'Feita' },
    cancelada: { tom: 'neutro', texto: 'Cancelada' },
  };
  const s = mapa[t.situacao];
  return <Selo tom={s.tom}>{s.texto}</Selo>;
}

function usePermissao(codigo: string): boolean {
  const { estado } = useSessao();
  return estado.fase === 'dentro' && estado.eu.permissoes.includes(codigo);
}

function useEu() {
  const { estado } = useSessao();
  return estado.fase === 'dentro' ? estado.eu.usuario : null;
}

type Usuario = { id: string; nome: string };

/** Quem pode ser responsavel: a equipe ativa do workspace (GET /api/v1/auth/equipe). */
function useResponsaveis(): Usuario[] {
  const eu = useEu();
  const q = useQuery({
    queryKey: ['comercial', 'equipe'],
    queryFn: () => ler<{ itens: Usuario[] }>('auth.equipe'),
    staleTime: 5 * 60_000,
  });
  const lista = [...(q.data?.itens ?? [])];
  // enquanto a equipe carrega (ou se falhar), quem esta logado ja aparece
  if (eu !== null && !lista.some((u) => u.id === eu.id)) {
    lista.unshift({ id: eu.id, nome: eu.nome });
  }
  return lista;
}

/** As escritas de um clique (concluir, adiar, reabrir), com o erro para mostrar. */
function useAcoes() {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const atualizar = () => qc.invalidateQueries({ queryKey: ['comercial'] });
  const m = useMutation({
    mutationFn: ({ tool, entrada }: { tool: string; entrada: Record<string, unknown> }) =>
      escrever<Tarefa>(tool, entrada),
    onSuccess: async () => { setErro(null); await atualizar(); },
    onError: (e) => setErro(mensagemDe(e)),
  });
  return {
    erro,
    ocupado: m.isPending,
    concluir: (t: Tarefa) => m.mutate({ tool: 'comercial.tarefa_concluir', entrada: { id: t.id } }),
    reabrir: (t: Tarefa) => m.mutate({ tool: 'comercial.tarefa_reabrir', entrada: { id: t.id } }),
    adiar: (t: Tarefa, vence: string) =>
      m.mutate({ tool: 'comercial.tarefa_editar', entrada: { id: t.id, vence_em: vence } }),
  };
}

/** Nao deixa o clique no botao abrir o painel da linha. */
function SemAbrir({ children }: { children: React.ReactNode }) {
  return (
    <span className="comercial-acoes" onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}>
      {children}
    </span>
  );
}

function LinkVinculo({ t }: { t: Tarefa }) {
  if (t.vinculo_tipo !== null && t.vinculo_id !== null) {
    const v = VINCULOS[t.vinculo_tipo];
    return (
      <SemAbrir>
        <Link className="comercial-link" to={`${v.rota}?id=${t.vinculo_id}`}>
          {t.pessoa_nome !== null ? `${v.rotulo}: ${t.pessoa_nome}` : `Abrir ${v.rotulo.toLowerCase()}`}
        </Link>
      </SemAbrir>
    );
  }
  if (t.pessoa_id !== null) {
    return (
      <SemAbrir>
        <Link className="comercial-link" to={`/pessoas?id=${t.pessoa_id}`}>
          {t.pessoa_nome ?? 'Abrir pessoa'}
        </Link>
      </SemAbrir>
    );
  }
  return <span className="comercial-fraco">Sem vínculo</span>;
}

function Adiar({ t, aoAdiar, desativado }: {
  t: Tarefa; aoAdiar: (t: Tarefa, vence: string) => void; desativado: boolean;
}) {
  const opcoes = [
    { id: '1h', rotulo: 'Daqui a 1 hora', vence: () => new Date(Date.now() + 3_600_000).toISOString() },
    { id: 'amanha', rotulo: 'Amanhã, 9h', vence: () => diaMais(1) },
    { id: '2d', rotulo: 'Em 2 dias, 9h', vence: () => diaMais(2) },
    { id: 'segunda', rotulo: 'Segunda, 9h', vence: proximaSegunda },
  ];
  return (
    <select className="comercial-adiar" aria-label={`Adiar ${t.titulo}`} value=""
      disabled={desativado}
      onChange={(e) => {
        const o = opcoes.find((x) => x.id === e.target.value);
        if (o !== undefined) aoAdiar(t, o.vence());
      }}>
      <option value="">Adiar</option>
      {opcoes.map((o) => <option key={o.id} value={o.id}>{o.rotulo}</option>)}
    </select>
  );
}

function colunas(acoes: ReturnType<typeof useAcoes>, podeConcluir: boolean,
  podeEditar: boolean): readonly Coluna<Tarefa>[] {
  return [
    {
      id: 'feito', titulo: '',
      celula: (t) => (
        <SemAbrir>
          {t.status === 'aberta' ? (
            <button type="button" className="comercial-check"
              aria-label={`Concluir ${t.titulo}`} title="Concluir"
              disabled={!podeConcluir || acoes.ocupado} onClick={() => acoes.concluir(t)}>
              <Check size={16} strokeWidth={2.25} aria-hidden="true" />
            </button>
          ) : (
            <button type="button" className="comercial-check comercial-check--feita"
              aria-label={`Reabrir ${t.titulo}`} title="Reabrir"
              disabled={!podeConcluir || acoes.ocupado} onClick={() => acoes.reabrir(t)}>
              <RotateCcw size={15} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </SemAbrir>
      ),
    },
    {
      id: 'tarefa', titulo: 'Tarefa',
      celula: (t) => (
        <span className="comercial-tarefa">
          <strong className={t.status === 'aberta' ? '' : 'comercial-riscado'}>{t.titulo}</strong>
          <span className="comercial-fraco">
            {TIPOS[t.tipo]}{t.descricao !== null ? ` · ${t.descricao}` : ''}
          </span>
        </span>
      ),
    },
    { id: 'vence', titulo: 'Vencimento', celula: (t) => <Vencimento t={t} /> },
    { id: 'vinculo', titulo: 'Vínculo', celula: (t) => <LinkVinculo t={t} /> },
    {
      id: 'responsavel', titulo: 'Responsável', secundaria: true,
      celula: (t) => t.status === 'feita'
        ? `Feita por ${t.concluida_por_tipo === 'chave_api' ? 'Igor' : t.concluida_por_nome ?? ''}`
        : t.responsavel_nome ?? <span className="comercial-fraco">Equipe</span>,
    },
    {
      id: 'adiar', titulo: '', alinhar: 'direita',
      celula: (t) => t.status === 'aberta' && podeEditar
        ? <SemAbrir><Adiar t={t} aoAdiar={acoes.adiar} desativado={acoes.ocupado} /></SemAbrir>
        : t.status === 'feita' ? <span className="comercial-fraco">{dataHora(t.concluida_em)}</span>
          : null,
    },
  ];
}

/** Titulo, tipo, vencimento e lembrete numa linha: o jeito rapido de anotar o retorno. */
function CriacaoRapida() {
  const qc = useQueryClient();
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState<TipoTarefa>('ligar');
  // proxima hora cheia, em Brasilia
  const [vence, setVence] = useState(() =>
    `${paraLocal(new Date(Date.now() + 3_600_000).toISOString()).slice(0, 13)}:00`);
  const [lembrete, setLembrete] = useState('15m');
  const [erro, setErro] = useState<ErroApi | Error | null>(null);
  const criar = useMutation({
    mutationFn: () => {
      const minutos = LEMBRETES.find((l) => l.id === lembrete)?.minutos ?? null;
      return escrever<Tarefa>('comercial.tarefa_criar', {
        titulo, tipo, vence_em: vence,
        ...(minutos !== null && vence !== '' ? { lembrar_em: menosMinutos(vence, minutos) } : {}),
      });
    },
    onSuccess: async () => {
      setErro(null);
      setTitulo('');
      await qc.invalidateQueries({ queryKey: ['comercial'] });
    },
    onError: (e) => setErro(e as Error),
  });
  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);
  return (
    <div className="comercial-rapida">
      <Formulario aoEnviar={() => criar.mutate()}>
        <Campo rotulo="Nova tarefa" erro={erroDo('titulo')}>
          {(id) => (
            <input id={id} value={titulo} placeholder="Ligar para a Marina sobre a Formação"
              autoComplete="off" onChange={(e) => setTitulo(e.target.value)} />
          )}
        </Campo>
        <Campo rotulo="Tipo" largura="terco">
          {(id) => (
            <select id={id} value={tipo} onChange={(e) => setTipo(e.target.value as TipoTarefa)}>
              {Object.entries(TIPOS).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Vencimento" largura="terco" erro={erroDo('vence_em')}>
          {(id) => (
            <input id={id} type="datetime-local" value={vence}
              onChange={(e) => setVence(e.target.value)} />
          )}
        </Campo>
        <Campo rotulo="Lembrete por e-mail" largura="terco" erro={erroDo('lembrar_em')}>
          {(id) => (
            <select id={id} value={lembrete} onChange={(e) => setLembrete(e.target.value)}>
              {LEMBRETES.map((l) => <option key={l.id} value={l.id}>{l.rotulo}</option>)}
            </select>
          )}
        </Campo>
        <div className="comercial-rapida__enviar">
          <Botao tipo="submit" variante="primario" desativado={criar.isPending || titulo.trim() === ''}>
            {criar.isPending ? 'Criando' : 'Criar tarefa'}
          </Botao>
        </div>
      </Formulario>
      {erro !== null && !(erro instanceof ErroApi && erro.falhas.some((f) => f.campo))
        && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}
    </div>
  );
}

type Pessoa = { id: string; nome: string; telefone_formatado: string | null };

/** Escolhe a pessoa da tarefa pela busca de Pessoas. */
function EscolherPessoa({ nome, aoEscolher }: {
  nome: string | null; aoEscolher: (p: Pessoa | null) => void;
}) {
  const [termo, setTermo] = useState('');
  const q = useQuery({
    queryKey: ['comercial', 'pessoas', termo],
    queryFn: () => ler<{ itens: Pessoa[] }>('pessoas.buscar', { termo, limite: 5 }),
    enabled: termo.trim().length >= 2,
  });
  if (nome !== null) {
    return (
      <div className="comercial-pessoa">
        <span>{nome}</span>
        <Botao variante="fantasma" aoClicar={() => aoEscolher(null)}>Trocar</Botao>
      </div>
    );
  }
  return (
    <div className="comercial-pessoa__busca">
      <Busca valor={termo} aoMudar={setTermo} dica="Buscar pessoa por nome ou telefone" />
      {q.isSuccess && q.data.itens.length === 0 && (
        <span className="comercial-fraco">Ninguém encontrado.</span>
      )}
      {q.data?.itens.map((p) => (
        <button key={p.id} type="button" className="comercial-opcao" onClick={() => aoEscolher(p)}>
          <strong>{p.nome}</strong>
          {p.telefone_formatado !== null && <span className="comercial-fraco">{p.telefone_formatado}</span>}
        </button>
      ))}
    </div>
  );
}

type Form = {
  titulo: string;
  tipo: TipoTarefa;
  descricao: string;
  vence: string;
  lembrar: string;
  responsavel: string;
  pessoa: { id: string; nome: string } | null;
};

const doRegistro = (t: Tarefa | null, euId: string): Form => ({
  titulo: t?.titulo ?? '',
  tipo: t?.tipo ?? 'ligar',
  descricao: t?.descricao ?? '',
  vence: t !== null ? paraLocal(t.vence_em) : `${diaMais(1)}T09:00`,
  lembrar: t !== null ? paraLocal(t.lembrar_em) : '',
  responsavel: t !== null ? t.responsavel_id ?? '' : euId,
  pessoa: t?.pessoa_id != null ? { id: t.pessoa_id, nome: t.pessoa_nome ?? 'Pessoa' } : null,
});

/** Painel de criar e editar com todos os campos. `tarefa` nulo = nova. */
function PainelTarefa({ tarefa, aoFechar }: { tarefa: Tarefa | null; aoFechar: () => void }) {
  const qc = useQueryClient();
  const eu = useEu();
  const responsaveis = useResponsaveis();
  const [inicial] = useState<Form>(() => doRegistro(tarefa, eu?.id ?? ''));
  const [form, setForm] = useState<Form>(inicial);
  const [erro, setErro] = useState<ErroApi | Error | null>(null);
  const aberta = tarefa === null || tarefa.status === 'aberta';
  const podeEditar = usePermissao(tarefa === null ? 'comercial.criar' : 'comercial.editar')
    && aberta;
  const podeConcluir = usePermissao('comercial.concluir');
  const podeCancelar = usePermissao('comercial.editar');
  const podeExcluir = usePermissao('comercial.excluir');

  const vinculoFixo = tarefa !== null && tarefa.vinculo_tipo !== null
    && tarefa.vinculo_tipo !== 'pessoa';

  const fim = async () => { await qc.invalidateQueries({ queryKey: ['comercial'] }); aoFechar(); };
  const falhou = (e: unknown) => setErro(e as Error);

  const salvar = useMutation({
    mutationFn: () => {
      const comum = {
        titulo: form.titulo,
        tipo: form.tipo,
        descricao: form.descricao.trim() === '' ? null : form.descricao,
        vence_em: form.vence,
        lembrar_em: form.lembrar === '' ? null : form.lembrar,
        responsavel_id: form.responsavel === '' ? null : form.responsavel,
      };
      if (tarefa === null) {
        return escrever<Tarefa>('comercial.tarefa_criar', {
          ...comum,
          ...(form.pessoa !== null
            ? { pessoa_id: form.pessoa.id, vinculo_tipo: 'pessoa', vinculo_id: form.pessoa.id }
            : {}),
        });
      }
      // so o que mudou: reenviar o vencimento igual nao pode contar como adiar
      const antes: Record<string, unknown> = {
        titulo: inicial.titulo, tipo: inicial.tipo,
        descricao: inicial.descricao.trim() === '' ? null : inicial.descricao,
        vence_em: inicial.vence, lembrar_em: inicial.lembrar === '' ? null : inicial.lembrar,
        responsavel_id: inicial.responsavel === '' ? null : inicial.responsavel,
      };
      const mudou = Object.fromEntries(
        Object.entries(comum).filter(([k, v]) => antes[k] !== v));
      // vinculo de outro tipo (lead, matricula) fica; so a pessoa solta muda aqui
      const pessoaMudou = form.pessoa?.id !== inicial.pessoa?.id;
      const pessoa = pessoaMudou && !vinculoFixo
        ? form.pessoa !== null
          ? { pessoa_id: form.pessoa.id, vinculo_tipo: 'pessoa', vinculo_id: form.pessoa.id }
          : { pessoa_id: null, vinculo_tipo: null, vinculo_id: null }
        : {};
      return escrever<Tarefa>('comercial.tarefa_editar', { id: tarefa.id, ...mudou, ...pessoa });
    },
    onSuccess: fim,
    onError: falhou,
  });
  const acao = useMutation({
    mutationFn: (tool: string) => escrever(tool, { id: tarefa!.id }),
    onSuccess: fim,
    onError: falhou,
  });

  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);
  const mudar = (c: 'titulo' | 'descricao' | 'vence' | 'lembrar' | 'responsavel') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [c]: e.target.value }));
  const ocupado = salvar.isPending || acao.isPending;

  return (
    <Painel
      aberto aoFechar={aoFechar}
      titulo={tarefa === null ? 'Nova tarefa' : tarefa.titulo}
      {...(tarefa !== null ? {
        subtitulo: tarefa.status === 'feita'
          ? `Feita em ${dataHora(tarefa.concluida_em)}`
          : `Vence em ${dataHora(tarefa.vence_em)}`,
      } : {})}
      rodape={(
        <>
          {tarefa !== null && podeExcluir && (
            <Botao variante="perigo" desativado={ocupado}
              aoClicar={() => { if (window.confirm(`Excluir "${tarefa.titulo}"?`)) acao.mutate('comercial.excluir'); }}>
              Excluir
            </Botao>
          )}
          {tarefa !== null && tarefa.status === 'aberta' && podeCancelar && (
            <Botao desativado={ocupado} aoClicar={() => acao.mutate('comercial.tarefa_cancelar')}>
              Cancelar tarefa
            </Botao>
          )}
          {tarefa !== null && tarefa.status !== 'aberta' && podeConcluir && (
            <Botao desativado={ocupado} aoClicar={() => acao.mutate('comercial.tarefa_reabrir')}>
              Reabrir
            </Botao>
          )}
          {tarefa !== null && tarefa.status === 'aberta' && podeConcluir && (
            <Botao desativado={ocupado} aoClicar={() => acao.mutate('comercial.tarefa_concluir')}>
              Concluir
            </Botao>
          )}
          {podeEditar && (
            <Botao variante="primario" desativado={ocupado} aoClicar={() => salvar.mutate()}>
              {salvar.isPending ? 'Salvando' : 'Salvar'}
            </Botao>
          )}
        </>
      )}
    >
      {tarefa !== null && tarefa.status !== 'aberta' && (
        <Aviso tom="neutro">
          {tarefa.status === 'feita' ? 'Tarefa concluída.' : 'Tarefa cancelada.'} Reabra para editar.
        </Aviso>
      )}
      {tarefa?.lembrete_enviado_em != null && (
        <Aviso tom="info">Lembrete enviado em {dataHora(tarefa.lembrete_enviado_em)}.</Aviso>
      )}
      {erro !== null && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}

      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Título" erro={erroDo('titulo')}>
          {(id) => <input id={id} value={form.titulo} onChange={mudar('titulo')}
            disabled={!podeEditar} autoComplete="off" />}
        </Campo>
        <Campo rotulo="Tipo" largura="metade">
          {(id) => (
            <select id={id} value={form.tipo} disabled={!podeEditar}
              onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value as TipoTarefa }))}>
              {Object.entries(TIPOS).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Responsável" largura="metade" erro={erroDo('responsavel_id')}>
          {(id) => (
            <select id={id} value={form.responsavel} onChange={mudar('responsavel')}
              disabled={!podeEditar}>
              <option value="">Equipe (sem responsável)</option>
              {responsaveis.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
              {tarefa?.responsavel_id != null
                && !responsaveis.some((u) => u.id === tarefa.responsavel_id) && (
                <option value={tarefa.responsavel_id}>{tarefa.responsavel_nome ?? 'Outro'}</option>
              )}
            </select>
          )}
        </Campo>
        <Campo rotulo="Vencimento" largura="metade" erro={erroDo('vence_em')}
          dica="Horário de Brasília">
          {(id) => <input id={id} type="datetime-local" value={form.vence}
            onChange={mudar('vence')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Lembrete por e-mail" largura="metade" erro={erroDo('lembrar_em')}
          dica="Vazio, sem lembrete">
          {(id) => <input id={id} type="datetime-local" value={form.lembrar}
            onChange={mudar('lembrar')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Pessoa" erro={erroDo('pessoa_id')}>
          {() => tarefa !== null && vinculoFixo ? (
            <div className="comercial-pessoa">
              <LinkVinculo t={tarefa} />
            </div>
          ) : podeEditar ? (
            <EscolherPessoa nome={form.pessoa?.nome ?? null}
              aoEscolher={(p) => setForm((f) => ({ ...f, pessoa: p === null ? null : { id: p.id, nome: p.nome } }))} />
          ) : (
            <span>{form.pessoa?.nome ?? 'Sem pessoa'}</span>
          )}
        </Campo>
        <Campo rotulo="Detalhes" erro={erroDo('descricao')}>
          {(id) => <textarea id={id} value={form.descricao} onChange={mudar('descricao')}
            disabled={!podeEditar} placeholder="O que foi combinado" />}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

type Filtro = {
  responsavel: string; // '' = todos, 'eu' = minhas, ou id
  tipo: string;
  status: string;
  termo: string;
};

/** As tres abas sao a mesma lista com filtros diferentes. */
function ListaTarefas({ modo }: { modo: 'dia' | 'todas' | 'concluidas' }) {
  const eu = useEu();
  const responsaveis = useResponsaveis();
  const acoes = useAcoes();
  const podeCriar = usePermissao('comercial.criar');
  const podeConcluir = usePermissao('comercial.concluir');
  const podeEditar = usePermissao('comercial.editar');
  const [aberta, setAberta] = useState<Tarefa | null | 'nova'>(null);
  // ?id=<uuid> abre a tarefa direto (e-mail de lembrete, bloco do Inicio)
  const [busca, setBusca] = useSearchParams();
  const idPedido = busca.get('id');
  useEffect(() => {
    if (idPedido === null) return;
    void ler<Lista>('comercial.tarefas_listar', { id: idPedido, limite: 1 })
      .then((r) => { if (r.itens[0] !== undefined) setAberta(r.itens[0]); })
      .catch(() => {});
  }, [idPedido]);
  const fechar = () => {
    setAberta(null);
    if (idPedido !== null) {
      const resto = new URLSearchParams(busca);
      resto.delete('id');
      setBusca(resto, { replace: true });
    }
  };
  const [f, setF] = useState<Filtro>({
    responsavel: '', tipo: '', status: modo === 'todas' ? 'aberta' : '', termo: '',
  });

  const entrada: Record<string, unknown> = {
    limite: 300,
    ...(modo === 'dia' ? { hoje: true, atrasadas: true } : {}),
    ...(modo === 'concluidas' ? { status: 'feita' } : {}),
    ...(modo === 'todas' && f.status !== '' ? { status: f.status } : {}),
    ...(modo !== 'concluidas' && f.tipo !== '' ? { tipo: f.tipo } : {}),
    ...(f.responsavel === 'eu' ? { minhas: true } : {}),
    ...(f.responsavel !== '' && f.responsavel !== 'eu' ? { responsavel_id: f.responsavel } : {}),
    ...(f.termo !== '' ? { termo: f.termo } : {}),
  };
  const q = useQuery({
    queryKey: ['comercial', 'tarefas', modo, entrada],
    queryFn: () => ler<Lista>('comercial.tarefas_listar', entrada),
  });
  const mudar = (c: keyof Filtro) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    setF((x) => ({ ...x, [c]: e.target.value }));

  const vazio = {
    dia: { titulo: 'Nada para hoje', texto: 'Nenhuma tarefa vencendo hoje ou atrasada. Bom trabalho.' },
    todas: { titulo: 'Nenhuma tarefa', texto: 'Mude os filtros ou crie uma tarefa.' },
    concluidas: { titulo: 'Nenhuma tarefa concluída', texto: 'As tarefas feitas aparecem aqui.' },
  }[modo];

  const atrasadas = q.data?.itens.filter((t) => t.situacao === 'atrasada').length ?? 0;

  return (
    <>
      {modo === 'dia' && podeCriar && <CriacaoRapida />}
      <Barra
        acoes={podeCriar && modo !== 'dia'
          ? <Botao variante="primario" aoClicar={() => setAberta('nova')}>Nova tarefa</Botao>
          : undefined}
      >
        <Busca valor={f.termo} aoMudar={(v) => setF((x) => ({ ...x, termo: v }))}
          dica="Buscar por título ou pessoa" />
        <select aria-label="Responsável" value={f.responsavel} onChange={mudar('responsavel')}>
          <option value="">Todos os responsáveis</option>
          {eu !== null && <option value="eu">Minhas</option>}
          {responsaveis.filter((u) => u.id !== eu?.id).map((u) => (
            <option key={u.id} value={u.id}>{u.nome}</option>
          ))}
        </select>
        {modo !== 'concluidas' && (
          <select aria-label="Tipo" value={f.tipo} onChange={mudar('tipo')}>
            <option value="">Todos os tipos</option>
            {Object.entries(TIPOS).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
          </select>
        )}
        {modo === 'todas' && (
          <select aria-label="Status" value={f.status} onChange={mudar('status')}>
            <option value="aberta">Abertas</option>
            <option value="feita">Feitas</option>
            <option value="cancelada">Canceladas</option>
            <option value="">Todos os status</option>
          </select>
        )}
      </Barra>

      {acoes.erro !== null && <Aviso tom="erro">{acoes.erro}</Aviso>}
      {modo === 'dia' && atrasadas > 0 && (
        <Aviso tom="aviso">
          {atrasadas === 1 ? '1 tarefa atrasada.' : `${atrasadas} tarefas atrasadas.`} Conclua ou adie.
        </Aviso>
      )}

      {q.isPending && <EstadoCarregando texto="Carregando tarefas" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && <EstadoVazio {...vazio} />}
      {q.isSuccess && q.data.itens.length > 0 && (
        <div className="comercial-lista">
          <Tabela colunas={colunas(acoes, podeConcluir, podeEditar)} linhas={q.data.itens}
            chave={(t) => t.id} aoAbrir={(t) => setAberta(t)} />
        </div>
      )}

      {aberta !== null && (
        <PainelTarefa key={aberta === 'nova' ? 'nova' : aberta.id}
          tarefa={aberta === 'nova' ? null : aberta} aoFechar={fechar} />
      )}
    </>
  );
}

/** Bloco do Inicio: o que vence hoje e o que ficou para tras. */
function BlocoTarefas() {
  const acoes = useAcoes();
  const podeConcluir = usePermissao('comercial.concluir');
  const q = useQuery({
    queryKey: ['comercial', 'tarefas', 'inicio'],
    queryFn: () => ler<Lista>('comercial.tarefas_listar', { hoje: true, atrasadas: true, limite: 50 }),
  });
  const itens = q.data?.itens ?? [];
  const atrasadas = itens.filter((t) => t.situacao === 'atrasada').length;
  return (
    <BlocoInicio titulo="Tarefas de hoje" numero={itens.length}
      tom={atrasadas > 0 ? 'erro' : itens.length > 0 ? 'aviso' : 'ok'}
      link={{ rotulo: 'Abrir tarefas', href: '/comercial?aba=hoje' }}>
      {q.isPending && <span className="ui-item__detalhe">Carregando</span>}
      {q.isError && <span className="ui-item__detalhe">{mensagemDe(q.error)}</span>}
      {q.isSuccess && itens.length === 0 && (
        <span className="ui-item__detalhe">Nenhuma tarefa para hoje.</span>
      )}
      {acoes.erro !== null && <span className="ui-item__detalhe">{acoes.erro}</span>}
      {itens.slice(0, 5).map((t) => (
        <div key={t.id} className="ui-item">
          <div className="ui-item__principal">
            <Link className="ui-item__nome" to={`/comercial?aba=hoje&id=${t.id}`}>{t.titulo}</Link>
            <span className="ui-item__detalhe">
              {[TIPOS[t.tipo], t.pessoa_nome, t.responsavel_nome].filter(Boolean).join(' · ')}
            </span>
          </div>
          <span className="comercial-acoes">
            <Vencimento t={t} />
            {podeConcluir && (
              <button type="button" className="comercial-check" title="Concluir"
                aria-label={`Concluir ${t.titulo}`} disabled={acoes.ocupado}
                onClick={() => acoes.concluir(t)}>
                <Check size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            )}
          </span>
        </div>
      ))}
    </BlocoInicio>
  );
}

export const tela: TelaDeModulo = {
  slug: 'comercial',
  rota: '/comercial',
  kicker: 'Follow-up',
  titulo: 'Tarefas',
  abas: [
    { id: 'hoje', rotulo: 'Hoje e atrasadas', conteudo: () => <ListaTarefas modo="dia" /> },
    { id: 'todas', rotulo: 'Todas', conteudo: () => <ListaTarefas modo="todas" /> },
    { id: 'concluidas', rotulo: 'Concluídas', conteudo: () => <ListaTarefas modo="concluidas" /> },
  ],
  blocosDoInicio: [{ id: 'tarefas', permissao: 'comercial.ver', conteudo: BlocoTarefas }],
  dicionario: { pt: {} },
};
